"use client";

/**
 * A trail's track on a map with where the client is: what is walked in green,
 * what is left in grey, arrows along both the way the trail is walked, the
 * start as a ring and the position as a blue dot.
 * The map is fitted to the track, and to the position too when it is near
 * enough to be worth showing on the same map.
 *
 * Under the map, the same thing sketched with no map at all: the map is shown
 * only once it has loaded, so where our tiles do not come -- no signal, no
 * WebGL -- the frame is left to the sketch, which needs nothing but the track.
 * The relief is the style's own hill shading.
 */
import { useEffect, useMemo, useState } from "react";
import { Layer, Marker, Source, useMap } from "react-map-gl/maplibre";
import { OurMap } from "@/components/ui/OurMap";
import type { Fix } from "@/components/live/liveTrail";
import type { Located, Measured } from "@/components/live/trailPosition";
import { ARROW_IMAGE, addArrowImage, arrowLayer } from "@/lib/maps";

const COLOURS = { walked: "#16a34a", left: "#5c655f", here: "#2f7fd6" };

// a position further from the track than this is left off the map, which
// would otherwise shrink the trail to a speck
const SAME_MAP = 15000;

const DOT_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26"><circle cx="13" cy="13" r="12" fill="${COLOURS.here}" fill-opacity="0.22"/><circle cx="13" cy="13" r="6.5" fill="${COLOURS.here}" stroke="#fff" stroke-width="2.5"/></svg>`,
)}`;
const START_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="5.5" fill="#fff" stroke="#1f2521" stroke-width="2.5"/></svg>`,
)}`;

type LatLng = { lat: number; lng: number };

/** the track as the part walked and the part left, split where the client is */
const split = (measured: Measured, located: Located | null) => {
  const path = measured.points.map(([lat, lng]) => ({ lat, lng }));
  if (!located) {
    return { walked: [] as LatLng[], left: path };
  }
  const here = { lat: located.point[0], lng: located.point[1] };
  return { walked: [...path.slice(0, located.index + 1), here], left: [here, ...path.slice(located.index + 1)] };
};

/** a line as GeoJSON, longitude first: the one place our [lat, lng] is turned round */
const lineOf = (path: LatLng[]) => ({
  type: "FeatureCollection" as const,
  features:
    path.length > 1
      ? [{ type: "Feature" as const, properties: {}, geometry: { type: "LineString" as const, coordinates: path.map((p) => [p.lng, p.lat]) } }]
      : [],
});

function Line({ id, path, colour, weight }: { id: string; path: LatLng[]; colour: string; weight: number }) {
  const data = useMemo(() => lineOf(path), [path]);
  return (
    <Source id={id} type="geojson" data={data}>
      <Layer id={id} type="line" layout={{ "line-join": "round", "line-cap": "round" }} paint={{ "line-color": colour, "line-width": weight, "line-opacity": 0.95 }} />
      <Layer {...arrowLayer(`${id}-arrows`, id, colour)} />
    </Source>
  );
}

/** the arrows' image, given when the map first asks for it (and again after a new style) */
function ArrowImage() {
  const { current: map } = useMap();
  useEffect(() => {
    if (!map) return;
    const libre = map.getMap();
    const give = (e: { id: string }) => {
      if (e.id === ARROW_IMAGE) addArrowImage(libre);
    };
    libre.on("styleimagemissing", give);
    return () => {
      libre.off("styleimagemissing", give);
    };
  }, [map]);
  return null;
}

/** fitted once to the track, and again when the position first comes onto the map */
function Fit({ path, fix }: { path: LatLng[]; fix: LatLng | null }) {
  const { current: map } = useMap();
  const withFix = !!fix;
  useEffect(() => {
    if (!map || !path.length) return;
    const all = fix ? [...path, fix] : path;
    const lngs = all.map((p) => p.lng);
    const lats = all.map((p) => p.lat);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 28, duration: 0 },
    );
    // the position moving is not a reason to move the map under someone's finger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, path, withFix]);
  return null;
}

/* eslint-disable @next/next/no-img-element */
function Pins({ start, fix }: { start: LatLng; fix: LatLng | null }) {
  return (
    <>
      <Marker longitude={start.lng} latitude={start.lat} anchor="center">
        <img src={START_ICON} width={16} height={16} alt="" draggable={false} />
      </Marker>
      {fix && (
        <Marker longitude={fix.lng} latitude={fix.lat} anchor="center" style={{ zIndex: 10 }}>
          <img src={DOT_ICON} width={26} height={26} alt="" draggable={false} />
        </Marker>
      )}
    </>
  );
}
/* eslint-enable @next/next/no-img-element */

/** the track and the position with no map under them, each drawn to the same scale */
function Sketch({ walked, left, fix }: { walked: LatLng[]; left: LatLng[]; fix: LatLng | null }) {
  const all = [...walked, ...left, ...(fix ? [fix] : [])];
  if (all.length < 2) {
    return null;
  }
  const lats = all.map((p) => p.lat);
  const lngs = all.map((p) => p.lng);
  const south = Math.min(...lats);
  const north = Math.max(...lats);
  const west = Math.min(...lngs);
  const east = Math.max(...lngs);
  // a degree of longitude is shorter than one of latitude, by the latitude's cosine
  const squeeze = Math.cos((((south + north) / 2) * Math.PI) / 180);
  const width = Math.max((east - west) * squeeze, 1e-6);
  const height = Math.max(north - south, 1e-6);
  const scale = 1000 / Math.max(width, height);
  const xy = (p: LatLng) => `${Math.round((p.lng - west) * squeeze * scale)},${Math.round((north - p.lat) * scale)}`;
  const line = (path: LatLng[]) => path.map(xy).join(" ");
  const pad = 70;
  const here = fix ? xy(fix).split(",") : null;
  const first = xy((walked.length ? walked : left)[0]).split(",");
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox={`${-pad} ${-pad} ${width * scale + pad * 2} ${height * scale + pad * 2}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="O percurso e a tua posição"
    >
      <polyline points={line(left)} fill="none" stroke={COLOURS.left} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {walked.length > 1 && <polyline points={line(walked)} fill="none" stroke={COLOURS.walked} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
      <circle cx={first[0]} cy={first[1]} r="16" fill="#fff" stroke="#1f2521" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      {here && <circle cx={here[0]} cy={here[1]} r="22" fill={COLOURS.here} stroke="#fff" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

export function LiveMap({ measured, fix, located }: { measured: Measured; fix: Fix | null; located: Located | null }) {
  const { walked, left } = useMemo(() => split(measured, located), [measured, located]);
  const path = useMemo(() => measured.points.map(([lat, lng]) => ({ lat, lng })), [measured]);
  // on the trail the dot sits on the track; away from it, where the phone says it is
  const here = useMemo(() => (located ? { lat: located.point[0], lng: located.point[1] } : fix ? { lat: fix.lat, lng: fix.lng } : null), [located, fix]);
  const near = !!here && (!!located || !fix || nearEnough(path, here));
  const shown = near ? here : null;
  // the map covers the sketch only once it has drawn
  const [drawn, setDrawn] = useState(false);

  if (!path.length) {
    return null;
  }
  return (
    <div className="relative aspect-[640/400] w-full overflow-hidden rounded-[12px] border border-line bg-ground">
      <Sketch walked={walked} left={left} fix={shown} />
      <div className={`absolute inset-0 transition-opacity ${drawn ? "opacity-100" : "opacity-0"}`}>
        <OurMap center={path[0]} zoom={12} cooperative onLoad={() => setDrawn(true)} onFailed={() => setDrawn(false)}>
          <ArrowImage />
          <Line id="live-left" path={left} colour={COLOURS.left} weight={4} />
          <Line id="live-walked" path={walked} colour={COLOURS.walked} weight={5} />
          <Fit path={path} fix={shown} />
          <Pins start={path[0]} fix={shown} />
        </OurMap>
      </div>
    </div>
  );
}

// roughly how far a position is from the track's first point, in metres: only
// to decide whether the two belong on one map
const nearEnough = (path: LatLng[], here: LatLng) => {
  const dy = (here.lat - path[0].lat) * 111320;
  const dx = (here.lng - path[0].lng) * 111320 * Math.cos((here.lat * Math.PI) / 180);
  return Math.hypot(dx, dy) < SAME_MAP;
};
